import React, { FC, useCallback, useRef, useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { FormInstance } from "antd";
import {
  AppButton,
  AuthLayout,
  Form,
  FORM_FIELD_TYPES,
  IFormItems,
} from "site/ui/base";
import { routeNames } from "utils";
import { FlexBoxHorizontal } from "common/base/flex-box";

const Description = styled.p`
  color: #666;
  font-size: 0.875rem;
  margin-bottom: 2rem;
`;

const ButtonContainer = styled.div`
  margin-top: 1.5rem;
`;

const LoginLink = styled.p`
  text-align: center;
  margin-top: 2rem;
  font-size: 0.875rem;
  color: #666;

  a {
    color: #5575f7;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export interface ForgetPasswordProps {
  onSendOtp: (email: string) => Promise<boolean>;
  onVerifyOtp: (email: string, otp: string) => Promise<string | undefined>;
  onMoveToResetPassword: (email: string, accessToken: string) => void;
}
export const ForgetPassword:FC<ForgetPasswordProps> = ({ onSendOtp, onVerifyOtp, onMoveToResetPassword }) => {
  const formRef = useRef<FormInstance<{email: string}>>(null);
  const otpFormRef = useRef<FormInstance<{otp: string}>>(null);
  const [loading, setLoading] = useState(false)
  const [email, setEmail] = useState("")
  const [page, setPage] = useState<"input" | "otp">("input");

  const formItems: IFormItems[] = [
    {
      fieldType: FORM_FIELD_TYPES.TEXT,
      itemProps: {
        name: "email",
        label: "Email address",
        rules: [{ required: true, message: "Please input your email!" }],
      },
      fieldProps: {
        placeholder: "esosa@example.art",
        size: "large",
      },
    },
  ];

  const handleSubmit = useCallback(async (values: { email: string }) => {
    setLoading(true)
    const success = await onSendOtp(values.email);
    setLoading(false)
    if (success) {
      setEmail(values.email);
      setPage("otp");
    }
  }, [page]);

  const handleReset = () => {
    formRef.current?.submit();
  };
  const handleOptSubmit = useCallback(async (values: { otp: string }) => {
    setLoading(true)
    const accessToken = await onVerifyOtp(email, values.otp);
    setLoading(false)
    if (accessToken) {
      onMoveToResetPassword(email, accessToken as string);
    }
  }, [page, email]);
  const handleOptReset = useCallback(() => {
    otpFormRef.current?.submit();
  }, [email]);

  if(page === "otp"){ 
    return (
      <AuthLayout title="Insert OTP to reset password?">
        <Description>
          We've sent a 6-digit OTP to your email. Please enter it below to verify your identity and reset your password.
        </Description>
        <Form 
            formRef={otpFormRef}
            formProps={{ 
              layout: "vertical",
              onFinish: handleOptSubmit,
            }} 
            items={[
                {
                    fieldType: FORM_FIELD_TYPES.TEXT,
                    itemProps: {
                        name: "otp",
                        label: "One Time Password",
                        rules: [{ required: true, message: "Please input the OTP sent to your email!" }],
                    },
                    fieldProps: {
                        placeholder: "Enter the 4-digit OTP",
                        size: "large",
                    },
                }
            ]}
          />
          <FlexBoxHorizontal>
            <AppButton style={{width: 100}} loading={loading} variant="secondary" fullWidth onClick={() => setPage("input")}>
              Back
            </AppButton>
            <AppButton loading={loading} variant="primary" fullWidth onClick={handleOptReset}>
              Verify otp
            </AppButton>
          </FlexBoxHorizontal>

      </AuthLayout>
    )
  }

  return (
    <AuthLayout title="Forgot password?">
      <Description>
        Don't worry, we've got you covered! Enter your email and we'll send you
        a secure link to reset your password.
      </Description>

      <Form
        formRef={formRef}
        items={formItems}
        formProps={{
          layout: "vertical",
          onFinish: handleSubmit,
        }}
      />

      <ButtonContainer>
        <AppButton loading={loading} variant="primary" fullWidth onClick={handleReset}>
          Send otp
        </AppButton>
      </ButtonContainer>

      <LoginLink>
        Remembered your password? <Link to={routeNames.login}>Get back in here</Link>
      </LoginLink>
    </AuthLayout>
  );
};
